export const refund = {
  slug: "refund-policy",
  title: "Payment and Refund Policy",
  updated: "4 November 2026",
  sections: [
    {
      h: null,
      paras: ["This policy forms part of the SolveDesk Terms and Conditions and applies to all payments made through the Website or directly to Solve Desk Service Private Limited."],
    },
    {
      h: "1. Prices",
      paras: [
        "1.1 All prices are in Indian Rupees and are exclusive of GST, which is charged at the applicable rate (currently 18% on most of our services).",
        "1.2 Prices shown on the Website may change at any time. The price applicable to you is the one shown at the time of your payment or in your signed order form.",
        "1.3 Government fees, stamp duty, courier charges and third-party charges are payable in addition, unless the plan expressly includes them.",
      ],
    },
    {
      h: "2. Payment terms",
      paras: [
        "2.1 All plans are payable in full in advance, unless a different schedule is agreed in writing.",
        "2.2 We accept payment through the payment gateway on the Website (UPI, debit card, credit card, net banking) and by bank transfer to our account notified on the invoice. We do not store your card details; payments are processed by our payment gateway partner.",
        "2.3 A tax invoice will be issued in the name and GSTIN provided by you. Please provide correct details before payment, as invoices cannot ordinarily be re-issued after the GST return for that month is filed.",
        "2.4 Services begin only after the payment is received in full and KYC is completed.",
        "2.5 If you deduct tax at source, you must deposit it and issue the TDS certificate within the time required by law; otherwise the amount deducted will be treated as unpaid.",
      ],
    },
    {
      h: "3. Renewals and late payment",
      paras: [
        "3.1 Renewal fees are payable before the expiry of the current term. We will send a reminder 15 days before expiry.",
        "3.2 If the renewal fee is not received by the due date, we may suspend the Services, including mail handling and use of the address, and the provisions on expiry in the Terms and Conditions will apply.",
        "3.3 Overdue amounts may carry interest at 1.5% per month from the due date until payment.",
      ],
    },
    {
      h: "4. Security deposit",
      paras: ["Where a plan requires a refundable security deposit, it will be refunded without interest within 30 days after the end of the Services, after adjusting unpaid fees, damages and charges, and after you submit proof of change of your registered office and GST address."],
    },
    {
      h: "5. Cancellation and refunds",
      table: {
        head: ["Service", "When you cancel", "Refund"],
        rows: [
          ["Virtual office plan", "Before KYC is approved and before any address document is issued", "Full refund, less a processing fee and payment gateway charges"],
          ["Virtual office plan", "After any address document (NOC, agreement, utility bill) is issued", "No refund, as the documents and partner commitments are already made"],
          ["Virtual office plan", "If we are unable to provide the address documents for reasons attributable to us", "Full refund of the plan fee"],
          ["Virtual office plan", "If GST or company registration is rejected by the authority", "Free change to another address in the same city, once, if rejection is due to the address documents. Rejection due to your documents or business details is not eligible."],
          ["Meeting room booking", "24 hours or more before the booking", "Full refund or credit for a future booking"],
          ["Meeting room booking", "Less than 24 hours before, or no-show", "No refund"],
          ["Add-on services (GST registration, return filing, company or LLP registration)", "Before work begins", "Full refund, less payment gateway charges"],
          ["Add-on services", "After work begins or a filing is made", "No refund of professional fees. Government fees once paid are not refundable."],
          ["Mail forwarding and courier charges", "After dispatch", "No refund"],
          ["Business signage", "After the signage is ordered or installed", "No refund"],
        ],
      },
      parasAfter: [
        "5.1 Fees for any unused part of a term are not refundable when you terminate for convenience or when we terminate because of your breach, false KYC or misuse of the address.",
        "5.2 Discounts, offers and free add-ons are not refundable and cannot be exchanged for cash.",
      ],
    },
    {
      h: "6. How to request a refund",
      paras: [
        "6.1 Send your request to booking@solvedesk.space or WhatsApp +91 62900 74471, with your name, invoice number, payment reference and reason.",
        "6.2 We will reply within 3 working days. Approved refunds will be made to the original payment method within 7 to 10 working days of approval. Bank or gateway timelines may add to this.",
        "6.3 GST charged on a refunded amount will be refunded or adjusted by a credit note, as permitted under GST law.",
      ],
    },
    {
      h: "7. Chargebacks and failed payments",
      paras: ["If a payment fails or is reversed by your bank, the Services will be suspended until the payment is made. Please contact us before raising a chargeback, so that we can resolve the issue."],
    },
    {
      h: "8. Contact",
      paras: ["For billing and refund queries: booking@solvedesk.space | +91 62900 74471"],
    },
  ],
};

export const privacy = {
  slug: "privacy-policy",
  title: "Privacy Policy",
  updated: "4 November 2026",
  sections: [
    {
      h: "1. Who we are",
      paras: [
        "1.1 This Privacy Policy explains how Solve Desk Service Private Limited (CIN: U68100WR2026PTC296275), operating SolveDesk (\"SolveDesk\", \"we\", \"us\"), collects, uses, shares and protects personal data when you use www.solvedesk.space or our services.",
        "1.2 We process personal data in accordance with the Digital Personal Data Protection Act, 2023, the rules made under it, and the Information Technology Act, 2000. For the purposes of that law, we are the Data Fiduciary for the personal data described here.",
        "1.3 By using the Website or our services, or by giving us your personal data, you consent to its processing as described in this policy.",
      ],
    },
    {
      h: "2. Personal data we collect",
      table: {
        head: ["Category", "Examples", "Source"],
        rows: [
          ["Contact data", "Name, phone number, WhatsApp number, email address", "You, through enquiry forms, calls and messages"],
          ["KYC and identity data", "PAN, Aadhaar (masked where possible), passport, photographs, address proof of directors, partners or proprietors", "You"],
          ["Business data", "Business name, constitution, certificate of incorporation, GSTIN, board resolutions, authorisation letters", "You"],
          ["Transaction data", "Plan purchased, invoices, payment status and reference numbers", "You and our payment gateway. We do not store full card or bank details"],
          ["Service data", "Mail received for you, call logs and messages taken under call answering, meeting-room bookings", "Our operations and partner centres"],
          ["Visit data", "Visitor register entries and CCTV footage at partner centres", "Partner centres"],
          ["Technical data", "IP address, browser and device type, pages visited, cookies", "Website analytics and cookies"],
        ],
      },
    },
    {
      h: "3. Why we use your personal data",
      items: [
        "To verify your identity and complete KYC before issuing address documents.",
        "To provide the services you buy, including address documents, mail handling, call answering, meeting rooms, signage and add-on registrations.",
        "To issue invoices, collect payments, process refunds and maintain accounts.",
        "To communicate with you about your plan, renewals, mail and service updates.",
        "To respond to verification visits, notices and lawful requests from government authorities.",
        "To keep the Website and partner centres secure and prevent fraud or misuse of addresses.",
        "To send you information about our services, only where you have agreed. You may opt out at any time.",
      ],
      parasAfter: ["We process personal data on the basis of your consent, and for legitimate uses permitted by law, such as compliance with legal obligations and orders of authorities."],
    },
    {
      h: "4. Who we share it with",
      paras: ["We do not sell your personal data. We share it only as needed with:"],
      items: [
        "Partner centres and property owners whose address you use, for issuing NOCs and agreements and for mail and visitor management.",
        "Solvelegalx.com and its professionals, for GST, company or LLP registration and other add-on services you request.",
        "Government authorities, including GST, Ministry of Corporate Affairs and tax departments, when you apply for registrations or when required by law.",
        "Service providers such as payment gateways, courier companies, cloud storage, email and messaging providers, who process data on our instructions.",
        "Professional advisers, auditors and courts, where needed to protect our legal rights.",
      ],
    },
    {
      h: "5. Storage, transfer and retention",
      paras: [
        "5.1 Your data is stored on secure servers and systems in India or with service providers who may store it outside India, where such transfer is permitted under Indian law.",
        "5.2 We keep personal data only for as long as needed for the purpose for which it was collected, and thereafter as required by law, for example for tax and accounting records. KYC documents are kept for the duration of your plan plus 8 years, unless a law requires a different period. CCTV footage at partner centres is kept as per the centre's policy, usually 30 to 90 days.",
        "5.3 When data is no longer needed, we erase it or make it anonymous.",
      ],
    },
    {
      h: "6. Security",
      paras: ["We use reasonable security safeguards, including access controls, encryption where appropriate and limited staff access, to protect your personal data. If a personal data breach occurs, we will notify you and the Data Protection Board of India as required by law. No system is completely secure, so please keep your login details and documents safe."],
    },
    {
      h: "7. Your rights",
      paras: ["Subject to applicable law, you have the right to:"],
      items: [
        "obtain a summary of the personal data we process about you and with whom it has been shared;",
        "correct, complete or update inaccurate or incomplete data;",
        "ask us to erase data that is no longer needed, unless we must keep it by law;",
        "withdraw your consent at any time, as easily as you gave it. Withdrawal does not affect processing already done, and we may be unable to continue the services that depend on that data;",
        "nominate another person to exercise your rights in the event of death or incapacity; and",
        "raise a grievance with us, and, if not satisfied, complain to the Data Protection Board of India.",
      ],
      parasAfter: ["To exercise these rights, write to our Grievance Officer at the address below."],
    },
    {
      h: "8. Cookies",
      paras: ["The Website uses cookies and similar tools to remember your preferences and to understand how visitors use the site. You can disable cookies in your browser settings, but some features of the Website may then not work properly."],
    },
    {
      h: "9. Children",
      paras: ["Our services are meant for adults and businesses. We do not knowingly collect personal data of anyone under 18 years of age."],
    },
    {
      h: "10. Third-party websites",
      paras: ["The Website may contain links to other websites, including payment gateways. Their privacy practices are governed by their own policies."],
    },
    {
      h: "11. Changes to this policy",
      paras: ["We may update this policy from time to time. The updated version will be published on this page with a new \"Last updated\" date. Where changes are significant, we will notify you by email or WhatsApp."],
    },
    {
      h: "12. Grievance Officer and contact",
      paras: [
        "Grievance Officer: Advocate Mrs. Diya, Solve Desk Service Private Limited (SolveDesk). Registered office: GRD FR, 1008 Laskarhat, Tagore Park, Tiljala, South 24 Parganas - 700039, West Bengal. Email: Legal@solvedesk.space. Phone: +91 62900 74471.",
        "We will acknowledge your grievance within 48 hours and try to resolve it within 30 days.",
      ],
    },
  ],
};
